import React, { useRef, useState } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "react-feather";
import { format } from "date-fns";

import { Modal, Button, Upload, P2, Select } from "~/components";
import { error, useMe } from "~/services";
import { useApi } from "~/hooks";

import { Form } from "@unform/web";

import * as Yup from "yup";

import { ModalView, MainForm, ErrorWrapper } from "./style";

const ConflictOfInterestByFamilyConnectionSettings = () => {
  const { t } = useTranslation("settings");

  const api = useApi();
  const alert = useAlert();
  const { user, group, newGroup } = useMe();

  const [isLoading, setLoading] = useState({
    upload: false,
    downloadLoadedData: false,
  });

  const [attachmentFile, setAttachmentFile] = useState(null);
  const [successText, setSuccessText] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDownloadLoadedDataModal, setShowDownloadLoadedDataModal] = useState(false);

  const uploadRef = useRef();

  const FR = new FileReader();

  const grupos = user.hierarquia?.map((item) => ({
    value: item.id,
    label: item.cliente,
  }));

  if (Object.entries(group).length === 0) {
    newGroup({
      value: user.hierarquia[0].id,
      label: user.hierarquia[0].cliente,
    });
  }

  if (grupos?.length > 1 && user.grupos && user.grupos.length > 0) {
    grupos.unshift({
      value: 0,
      label: user.grupos[0].grupo,
    });
  }

  const clearSuccessText = () => setTimeout(() => setSuccessText(null), 500);

  const handleClick = () => {
    if (!successText) {
      uploadRef.current.submitForm();
      return;
    }

    setShowUploadModal(false);
    clearSuccessText();
  };

  const handleClose = () => {
    setShowUploadModal(false);
    clearSuccessText();
  };

  const downloadLoadedDataSpreadsheet = async () => {
    setLoading({ downloadLoadedData: true });

    const clientId = group.value;
    const groupId = user.grupos[0]?.idgrupo;
    
    try {
      await api()
        .get(`/settings/family-connection-conflict/spreadsheet?from_scratch=0&clientId=${clientId}&groupId=${groupId}`, {
          responseType: "blob",
        })
        .then(async (res) => {
          const buffer = await res.data.arrayBuffer();

          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = "loaded_family_connection_conflict_spreadsheet.xlsx";
          document.body.appendChild(link);

          link.click();

          alert.success(t("conflict_of_interest_by_family_connection.download.success"));

          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        });
    } catch (error) {
      alert.error(`${t("conflict_of_interest_by_family_connection.download.error")}: ${error}`);
    } finally {
      setLoading({ downloadLoadedData: false });
      setShowDownloadLoadedDataModal(false);
    }
  };

  const upload = async (data) => {
    setLoading({ upload: true });
    await Yup.object()
      .shape({
        attachment: Yup.mixed().required(
          t("conflict_of_interest_by_family_connection.validations.file_required")
        ),
      })
      .validate(data, { abortEarly: false })
      .then(async () => {
        const clientId = group.value;
        const groupId = user.grupos[0]?.idgrupo;

        if (!clientId && !groupId) {
          alert.error(t("conflict_of_interest_by_family_connection.upload.error"));
          return;
        }

        const toSubmit = {
          file: attachmentFile,
          size: data.attachment.size,
          mimeType: data.attachment.type,
          originalName: data.attachment.name,
          groupId: groupId ? groupId : undefined,
          clientId: clientId ? clientId : undefined,
          extension: data.attachment.name.split(".")[1],
        };

        await api()
          .post(`/settings/family-connection-conflict/spreadsheet`, toSubmit)
          .then(({ data }) => {
            if (data.type === "success") setSuccessText(data.message);
            setAttachmentFile(null);
          })
          .catch(({ response }) => {
            let error = null;

            if (response?.data?.message) {
              error = (
                <ErrorWrapper>
                  <span>{t("conflict_of_interest_by_family_connection.upload.error")}:</span>
                  <span>{response?.data?.message}</span>
                </ErrorWrapper>
              );
            }
            
            alert.error(error);
          })
          .finally(() => {
            setLoading({ upload: false });
          });
      })
      .catch((err) => {
        alert.error(err.message);
        error(err, uploadRef);
      })
      .finally(() => {
        setLoading({ upload: false });
      });
  };

  FR.onload = (ev) => {
    setAttachmentFile(ev.target.result);
  };

  return (
    <MainForm>
      <Button
        onClick={() => setShowDownloadLoadedDataModal(true)}
        label={t("conflict_of_interest_by_family_connection.download_loaded_data_button")}
      />

      <Button
        label={t("conflict_of_interest_by_family_connection.upload_button")}
        onClick={() => {
          uploadRef.current.reset();
          setShowUploadModal(true);
        }}
      />

      <Form ref={uploadRef} onSubmit={upload}>
        <Modal
          hasActions
          show={showUploadModal}
          goBackOnCancel={handleClose}
          onShow={setShowUploadModal}
          title={t(
            `${
              successText
                ? "conflict.upload.success"
                : "conflict.upload_modal.title"
            }`
          )}
          showCancel={!successText}
          action={{
            onClick: handleClick,
            loading: isLoading.upload,
            label: t(
              `${
                successText
                  ? "conflict.upload_modal.close_button"
                  : "conflict.upload_modal.primary_button"
              }`
            ),
          }}
        >
          <ModalView>
            {!successText ? (
              <>
                <P2>{t("conflict_of_interest_by_family_connection.upload_modal.description")}</P2>
                <Upload
                  ext="xlsx"
                  isDocument
                  onInput={(evt) => {
                    FR.readAsDataURL(evt.target.files[0]);
                  }}
                  name={"attachment"}
                />
              </>
            ) : (
              <P2>{successText}</P2>
            )}
          </ModalView>
        </Modal>
      </Form>

      
      <Modal
        show={showDownloadLoadedDataModal}
        onShow={setShowDownloadLoadedDataModal}
        hasActions
        title={t("conflict_of_interest_by_family_connection.download_modal.title")}
        action={{
          label: t("conflict_of_interest_by_family_connection.download_modal.primary_button"),
          onClick: async () => downloadLoadedDataSpreadsheet(),
          loading: isLoading.downloadLoadedData,
        }}
      >
        <ModalView>
          <P2>{t("conflict_of_interest_by_family_connection.download_modal.description")}</P2>
        </ModalView>
      </Modal>
    </MainForm>
  );
};

export default ConflictOfInterestByFamilyConnectionSettings;
